import clsx from 'clsx'
import dynamic from 'next/dynamic'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { ProxyBannerProps } from './ProxyBanner'

import { LanguageChangeModal } from '@/components/features/auth/LanguageChangeModal'
import { CartWithCount } from '@/components/features/cart/CartWithCount'
import { FavoriteIcon } from '@/components/icons/FavoriteIcon'
import { MenuFilledIcon } from '@/components/icons/MenuFilled'
import { ScheduleOutlinedIcon } from '@/components/icons/ScheduleOutlined'
import { SearchFilledIcon } from '@/components/icons/SearchFilled'
import { GlobalMenu, Icon, IconWithLabel, Image } from '@/components/ui'
import { ROUTE } from '@/const'
import { IS_PRODUCTION } from '@/const/env'
import { useLimitedMode, useLogout } from '@/hooks/features/auth'
import { useMyStore } from '@/hooks/features/store'
import { useModal } from '@/hooks/util'

import { MaxWidthLayout } from '../MaxWidthLayout'
import { HeaderColumn } from './HeaderColumn'
import { HeaderIconLink } from './HeaderIconLink'
import { HeaderProps } from './type'

const ProxyBanner = dynamic<ProxyBannerProps>(
  () => import('./ProxyBanner').then((m) => m.ProxyBanner),
  { ssr: false }
)

export const Header = ({
  withSearch = true,
  withMenu = true,
  withLinks = true,
  disableLogoLink = false,
}: HeaderProps) => {
  const router = useRouter()
  const { mode, isLimitedTerm } = useLimitedMode()

  const { data: myStore } = useMyStore({})
  const { t } = useTranslation()

  const logoutMutation = useLogout({})

  const globalMenu = useModal()
  const languageChangeModal = useModal()

  const handleChangeAgency = useCallback(() => {
    const isOnSelectStore = router.pathname === ROUTE.selectStore
    const next = isOnSelectStore
      ? (router.query.next as string) ?? '/'
      : router.asPath

    router.push(`${ROUTE.selectStore}?next=${encodeURIComponent(next)}`)
  }, [router])

  const handleLogout = useCallback(() => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        router.push(ROUTE.login)
        globalMenu.close()
      },
    })
  }, [globalMenu, logoutMutation, router])

  const isLoading = useMemo(
    () => logoutMutation.isLoading,
    [logoutMutation.isLoading]
  )

  const showRightLinks = withLinks && (mode === 'normal' || !isLimitedTerm)

  return (
    <>
      <div
        className={clsx(
          'tw-sticky tw-top-0',
          'tw-bg-accentBlue-50',
          'tw-z-header',
          'tw-border-b tw-border-gray-300'
        )}
      >
        <MaxWidthLayout>
          <div
            className={clsx(
              'tw-w-full tw-h-20',
              'tw-flex tw-items-center tw-justify-between',
              'tw-px-3'
            )}
          >
            {/* left */}
            <HeaderColumn isFlex={true} justify={'start'}>
              {withMenu && (
                <button
                  className={clsx(
                    'tw-flex tw-items-center',
                    'hover:tw-opacity-hover',
                    'tw-transition tw-duration-stateChange'
                  )}
                  onClick={globalMenu.open}
                >
                  <IconWithLabel
                    label={t('common.menu.title')}
                    showLabelSp={false}
                  >
                    <Icon icon={MenuFilledIcon} size={'4xl'} />
                  </IconWithLabel>
                </button>
              )}
            </HeaderColumn>
            {/* center */}
            <HeaderColumn isFlex={true} justify={'center'} align={'center'}>
              <div className="tw-flex tw-flex-col tw-gap-1 tw-items-center">
                <div
                  className={clsx(
                    'tw-w-fit tw-flex tw-items-center',
                    IS_PRODUCTION ? 'tw-h-10' : 'tw-h-8'
                  )}
                >
                  {disableLogoLink ? (
                    <Image src="/logo.svg" alt="altair-logo" fit="contain" />
                  ) : (
                    <NextLink href={ROUTE.top} className="tw-h-[stretch]">
                      <Image src="/logo.svg" alt="altair-logo" fit="contain" />
                    </NextLink>
                  )}
                </div>
                {!IS_PRODUCTION && (
                  <div className="tw-text-xs tw-text-center">【開発環境】</div>
                )}
              </div>
            </HeaderColumn>
            {/* right */}
            <HeaderColumn isFlex={true} justify={'end'}>
              {showRightLinks && (
                <>
                  <HeaderIconLink
                    href={ROUTE.orderHistories}
                    label={t('order.history.title')}
                    showSp={false}
                  >
                    <Icon icon={ScheduleOutlinedIcon} size={'4xl'} />
                  </HeaderIconLink>
                  <HeaderIconLink
                    href={ROUTE.favorites}
                    label={t('favorite.title')}
                    showSp={false}
                  >
                    <FavoriteIcon size={32} type="empty" />
                  </HeaderIconLink>
                  {withSearch && (
                    <HeaderIconLink
                      href={ROUTE.search}
                      label={t('search.title')}
                      showSp={false}
                    >
                      <Icon icon={SearchFilledIcon} size={'4xl'} />
                    </HeaderIconLink>
                  )}
                  <NextLink href={ROUTE.cart}>
                    <span
                      className={clsx(
                        'tw-inline-flex tw-items-center',
                        'tw-transition tw-duration-stateChange',
                        'hover:tw-opacity-hover'
                      )}
                    >
                      <CartWithCount />
                    </span>
                  </NextLink>
                </>
              )}
            </HeaderColumn>
          </div>
        </MaxWidthLayout>
        <ProxyBanner onClickChange={handleChangeAgency} />
      </div>
      <GlobalMenu
        isOpen={globalMenu.isOpen}
        isLoading={isLoading}
        isRequirePasswordReset={false}
        showUploadOrder={false}
        showImportOrder={!!myStore?.importOrder}
        showInvoice={false}
        showOrder={mode !== 'limit-order' || !isLimitedTerm}
        showOrderHistory={mode !== 'limit-order' || !isLimitedTerm}
        storeName={myStore?.storeName}
        onClickLogout={handleLogout}
        onClickClose={globalMenu.close}
        onClickLanguage={languageChangeModal.open}
      />
      <LanguageChangeModal
        isOpen={languageChangeModal.isOpen}
        onClose={languageChangeModal.close}
      />
    </>
  )
}
